import { Trans, useTranslation } from 'react-i18next';
import {
  insurancePayoutCents,
  insurancePremiumCents,
  rateLabel,
  type InsuranceHolder,
  type InsuranceOffer,
} from '@gg/shared';
import { Badge, Card, CheckIcon, SelectableCard, TextField } from '@/components/ui';
import { formatPriceExact } from '@/lib/i18n';

/**
 * Beticketing cancellation insurance on screen 02 (P-19, decision 0025): the Assur Connect
 * kit's banner, its contractual texts word for word (the FR kit in French, the EN kit in
 * English), rebuilt with the app's components. Compact on a phone: the tagline,
 * price, notice link and opt-in stay visible; the kit's other selling points (badge,
 * sub-tagline, intro, "tous risques sauf" box, perks) fold under "En savoir plus", in the kit's
 * order. Shown under the voucher, which the premium follows. Not selected by default: the buyer
 * ticks the option to take it, and no confirmation is asked when they don't. Once ticked, the
 * insured person's name is asked (the insurer needs a named holder); unticked, the card says
 * the registration can't be refunded without it.
 *
 * `insuredCents`: the formats after the voucher, which the premium and payout are computed
 * from here for display; the server recomputes the premium it charges (invariant 2).
 */
export function InsuranceCard({
  offer,
  insuredCents,
  checked,
  onChange,
  holder,
  onHolderChange,
  holderErrors,
}: {
  offer: InsuranceOffer;
  insuredCents: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
  holder: InsuranceHolder;
  onHolderChange?: (holder: InsuranceHolder) => void;
  holderErrors?: { firstName?: boolean; lastName?: boolean };
}) {
  const { t, i18n } = useTranslation();
  const premium = formatPriceExact(insurancePremiumCents(insuredCents, offer.rateBps));
  const payout = formatPriceExact(insurancePayoutCents(insuredCents));
  const termsUrl =
    i18n.language === 'en' ? (offer.termsUrlEn ?? offer.termsUrlFr) : offer.termsUrlFr;
  const strong = <strong className="font-bold text-ink" />;

  return (
    <Card className="gap-0! overflow-hidden p-0!" aria-labelledby="insurance-title">
      <div className="flex items-center justify-between gap-4 border-b border-line px-4 py-3">
        <h2 id="insurance-title" className="m-0 text-lg leading-tight font-bold">
          {t('insurance.title')}
        </h2>
        <img
          src="/insurance/logo-assur-connect.png"
          alt={t('insurance.logoAlt')}
          width={65}
          height={40}
          className="h-10 w-auto shrink-0"
        />
      </div>

      <div className="flex flex-col gap-3 px-4 py-4">
        <p className="m-0 text-base leading-snug font-semibold">
          <Trans
            i18nKey="insurance.tagline"
            values={{ payout }}
            components={{ strong: <strong className="text-accent" /> }}
          />
        </p>
        <p className="m-0 text-sm text-ink-muted">
          <Trans
            i18nKey="insurance.price"
            values={{
              premium,
              rate: rateLabel(offer.rateBps, i18n.language === 'en' ? 'en' : 'fr'),
            }}
            components={{
              strong: <strong className="font-display text-price font-bold text-ink" />,
              rate: <span className="text-helper" />,
            }}
          />
        </p>

        <details className="group">
          <summary className="cursor-pointer list-none text-sm font-semibold text-accent hover:text-accent-hover [&::-webkit-details-marker]:hidden">
            <span className="group-open:hidden">{t('insurance.more')}</span>
            <span className="hidden group-open:inline">{t('insurance.less')}</span>
          </summary>
          <div className="mt-3 flex flex-col gap-3">
            <Badge tone="accent" keepCase className="self-start">
              {t('insurance.badge')}
            </Badge>
            <p className="m-0 text-sm text-ink-muted">{t('insurance.taglineSub')}</p>
            <p className="m-0 text-sm font-bold">{t('insurance.intro')}</p>
            <p className="m-0 rounded-control border border-line border-l-[3px] border-l-accent bg-surface px-3.5 py-3 text-sm">
              <Trans i18nKey="insurance.allRisks" components={{ strong }} />
            </p>
            <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-sm font-semibold">
              {(['perk1', 'perk2', 'perk3'] as const).map((k) => (
                <li key={k} className="flex items-center gap-2">
                  <CheckIcon size={16} className="shrink-0 text-success" />
                  {t(`insurance.${k}`)}
                </li>
              ))}
            </ul>
          </div>
        </details>

        <p className="m-0 text-helper text-ink-muted">
          <Trans
            i18nKey="insurance.terms"
            components={{
              notice: (
                <a
                  href={termsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold"
                />
              ),
            }}
          />
        </p>

        <SelectableCard
          title={<span className="text-base">{t('insurance.optIn', { premium })}</span>}
          details={t('insurance.optInNote')}
          checked={checked}
          selected={checked}
          onChange={(e) => onChange(e.target.checked)}
        />

        {!checked && <p className="m-0 text-helper text-ink-muted">{t('insurance.noRefund')}</p>}

        {checked && onHolderChange && (
          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-1 p-0 text-sm font-bold">{t('insurance.holder.title')}</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label={t('insurance.holder.firstName')}
                autoComplete="given-name"
                maxLength={80}
                value={holder.firstName}
                error={holderErrors?.firstName && t('insurance.holder.required')}
                onChange={(e) => onHolderChange({ ...holder, firstName: e.target.value })}
              />
              <TextField
                label={t('insurance.holder.lastName')}
                autoComplete="family-name"
                maxLength={80}
                value={holder.lastName}
                error={holderErrors?.lastName && t('insurance.holder.required')}
                onChange={(e) => onHolderChange({ ...holder, lastName: e.target.value })}
              />
            </div>
            <span className="text-helper text-ink-muted">{t('insurance.holder.hint')}</span>
          </fieldset>
        )}
      </div>
    </Card>
  );
}
